<script>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import QuotesAPI from 'dashboard/api/quotes';
import MessageApi from 'dashboard/api/inbox/message';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import { emitter } from 'shared/helpers/mitt';

export default {
  name: 'QuoteForm',
  components: {
    NextButton,
    Spinner,
  },
  props: {
    quote: {
      type: Object,
      required: true,
    },
    products: {
      type: Array,
      default: () => [],
    },
    conversationId: {
      type: [Number, String],
      required: true,
    },
    contactName: {
      type: String,
      default: '',
    },
    contactPhone: {
      type: String,
      default: '',
    },
  },
  emits: ['saved', 'close', 'message-sent'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const isLoading = ref(false);
    const isSaving = ref(false);
    const isSending = ref(false);
    const items = ref([]);
    const deliveryAddress = ref('');
    const locationLink = ref('');
    const observations = ref('');
    const discount = ref(0);
    const searchQuery = ref('');
    const showProductSearch = ref(false);
    const searchInputRef = ref(null);
    const quickAddInputRef = ref(null);

    // Quick add item state
    const quickAddName = ref('');
    const quickAddPrice = ref('');
    const quickAddQty = ref(1);
    const isAddingItem = ref(false);

    // Expanded item for editing (only one at a time)
    const expandedItemId = ref(null);

    const filteredProducts = computed(() => {
      if (!searchQuery.value) return props.products.slice(0, 10);
      const query = searchQuery.value.toLowerCase();
      return props.products.filter(
        p => p.name.toLowerCase().includes(query) ||
             (p.sku && p.sku.toLowerCase().includes(query))
      ).slice(0, 10);
    });

    const subtotal = computed(() => {
      return items.value.reduce((sum, item) => sum + Number(item.total || 0), 0);
    });

    const total = computed(() => {
      return Math.max(0, subtotal.value - Number(discount.value || 0));
    });

    const fetchItems = async () => {
      isLoading.value = true;
      try {
        const response = await QuotesAPI.getItems(props.quote.id);
        const loadedItems = response.data || [];
        items.value = loadedItems.map(item => ({
          ...item,
          unit_price: Number(item.unit_price || 0),
          quantity: Number(item.quantity || 1),
          discount: Number(item.discount || 0),
          total: Number(item.total || 0),
        }));
      } catch (error) {
        console.error('Error loading items:', error);
      } finally {
        isLoading.value = false;
      }
    };

    const addProduct = async (product) => {
      try {
        const response = await QuotesAPI.createItem(props.quote.id, {
          item: {
            product_id: product.id,
            product_name: product.name,
            product_description: product.description || '',
            unit_price: product.current_price || product.price || 0,
            quantity: 1,
            discount: 0,
          },
        });
        const newItem = response.data;
        items.value.push({
          ...newItem,
          unit_price: Number(newItem.unit_price || 0),
          quantity: Number(newItem.quantity || 1),
          discount: Number(newItem.discount || 0),
          total: Number(newItem.total || 0),
        });
        searchQuery.value = '';
      } catch (error) {
        useAlert(t('QUOTES.API.ERROR_SAVING'));
      }
    };

    // Quick add custom item
    const quickAddItem = async (event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      if (!quickAddName.value.trim()) {
        quickAddInputRef.value?.focus();
        return;
      }

      if (isAddingItem.value) return;

      isAddingItem.value = true;
      try {
        const price = parseFloat(quickAddPrice.value) || 0;
        const qty = parseInt(quickAddQty.value) || 1;

        const response = await QuotesAPI.createItem(props.quote.id, {
          item: {
            product_name: quickAddName.value.trim(),
            unit_price: price,
            quantity: qty,
            discount: 0,
          },
        });
        const newItem = response.data;
        items.value.push({
          ...newItem,
          unit_price: Number(newItem.unit_price || 0),
          quantity: Number(newItem.quantity || 1),
          discount: Number(newItem.discount || 0),
          total: Number(newItem.total || 0),
        });

        // Reset quick add fields
        quickAddName.value = '';
        quickAddPrice.value = '';
        quickAddQty.value = 1;

        // Focus back on name input for next item
        await nextTick();
        quickAddInputRef.value?.focus();
      } catch (error) {
        useAlert(t('QUOTES.API.ERROR_SAVING'));
      } finally {
        isAddingItem.value = false;
      }
    };

    // Handle Enter key in quick add fields
    const handleQuickAddKeydown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        quickAddItem(e);
      }
    };

    const updateItem = async (item) => {
      try {
        item.total = (Number(item.unit_price || 0) * Number(item.quantity || 1)) - Number(item.discount || 0);
        await QuotesAPI.updateItem(props.quote.id, item.id, {
          item: {
            product_name: item.product_name,
            unit_price: item.unit_price,
            quantity: item.quantity,
            discount: item.discount,
            observation: item.observation,
          },
        });
      } catch (error) {
        useAlert(t('QUOTES.API.ERROR_SAVING'));
      }
    };

    const removeItem = async (item, event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      try {
        await QuotesAPI.deleteItem(props.quote.id, item.id);
        items.value = items.value.filter(i => i.id !== item.id);
      } catch (error) {
        useAlert(t('QUOTES.API.ERROR_DELETING'));
      }
    };

    // Quick quantity adjustments
    const incrementQty = (item, event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      item.quantity = (item.quantity || 1) + 1;
      recalculateItemTotal(item);
      updateItem(item);
    };

    const decrementQty = (item, event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (item.quantity > 1) {
        item.quantity = item.quantity - 1;
        recalculateItemTotal(item);
        updateItem(item);
      }
    };

    const toggleItemExpanded = (itemId) => {
      expandedItemId.value = expandedItemId.value === itemId ? null : itemId;
    };

    const saveQuote = async () => {
      isSaving.value = true;
      try {
        await QuotesAPI.update(props.quote.id, {
          quote: {
            delivery_address: deliveryAddress.value,
            location_link: locationLink.value,
            observations: observations.value,
            discount: discount.value,
          },
        });
      } catch (error) {
        useAlert(t('QUOTES.API.ERROR_SAVING'));
      } finally {
        isSaving.value = false;
      }
    };

    const formatCurrency = (value) => {
      return `R$ ${Number(value || 0).toFixed(2)}`;
    };

    const recalculateItemTotal = (item) => {
      item.total = (Number(item.unit_price || 0) * Number(item.quantity || 1)) - Number(item.discount || 0);
    };

    const formatQuoteMessage = () => {
      let message = `📋 *ORÇAMENTO #${props.quote.id}*\n\n`;

      if (props.contactName) {
        message += `👤 *Cliente:* ${props.contactName}\n`;
      }
      if (props.contactPhone) {
        message += `📞 *Telefone:* ${props.contactPhone}\n`;
      }

      message += `\n📦 *ITENS:*\n`;
      items.value.forEach((item, index) => {
        const itemTotal = formatCurrency(item.total);
        const unitPrice = formatCurrency(item.unit_price);
        message += `${index + 1}. ${item.product_name}\n`;
        if (item.product_description) {
          message += `   _${item.product_description}_\n`;
        }
        message += `   ${unitPrice} x ${item.quantity} = ${itemTotal}\n`;
        if (item.discount > 0) {
          message += `   _(Desconto: ${formatCurrency(item.discount)})_\n`;
        }
        if (item.observation) {
          message += `   _Obs: ${item.observation}_\n`;
        }
      });

      message += `\n━━━━━━━━━━━━━━━━━━━━\n`;
      message += `💰 *Subtotal:* ${formatCurrency(subtotal.value)}\n`;

      if (discount.value > 0) {
        message += `🎁 *Desconto:* ${formatCurrency(discount.value)}\n`;
      }

      message += `✅ *TOTAL: ${formatCurrency(total.value)}*\n`;

      if (deliveryAddress.value) {
        message += `\n📍 *Endereço de entrega:*\n${deliveryAddress.value}\n`;
      }

      if (observations.value) {
        message += `\n📝 *Observações:*\n${observations.value}\n`;
      }

      return message;
    };

    const sendQuoteToConversation = async () => {
      if (items.value.length === 0) {
        useAlert(t('QUOTES.SEND.NO_ITEMS'));
        return;
      }

      isSending.value = true;
      try {
        // Save quote first
        await saveQuote();

        const message = formatQuoteMessage();
        await MessageApi.create({
          conversationId: props.conversationId,
          message: message,
          private: false,
        });
        useAlert(t('QUOTES.SEND.SUCCESS'));
        emit('message-sent');
      } catch (error) {
        console.error('Error sending quote:', error);
        useAlert(t('QUOTES.SEND.ERROR'));
      } finally {
        isSending.value = false;
      }
    };

    const handleClose = () => {
      emit('close');
    };

    // Focus search input when product search is shown
    watch(showProductSearch, async (value) => {
      if (value) {
        await nextTick();
        searchInputRef.value?.focus();
      }
    });

    watch(() => props.quote, (newQuote) => {
      if (newQuote) {
        deliveryAddress.value = newQuote.delivery_address || '';
        locationLink.value = newQuote.location_link || '';
        observations.value = newQuote.observations || '';
        discount.value = newQuote.discount || 0;
        fetchItems();
      }
    }, { immediate: true });

    // Get the internal conversation ID from the quote
    const internalConversationId = computed(() => {
      // The quote object has the internal conversation ID stored in conversation_internal_id
      // We need to match against this, not the display_id (conversation_id)
      return props.quote?.conversation_internal_id;
    });

    // Listen for delivery address updates from message context menu
    const handleDeliveryAddressUpdate = async (data) => {
      // Compare with both display_id and internal conversation_id
      const matchesDisplayId = data.conversationId && String(data.conversationId) === String(props.conversationId);
      const matchesInternalId = data.conversationId && String(data.conversationId) === String(internalConversationId.value);

      if (matchesDisplayId || matchesInternalId) {
        const newAddress = data.address || '';
        if (deliveryAddress.value) {
          deliveryAddress.value = deliveryAddress.value + '\n' + newAddress;
        } else {
          deliveryAddress.value = newAddress;
        }
        await nextTick();
        await saveQuote();
      }
    };

    // Listen for location link updates from message context menu
    const handleLocationLinkUpdate = async (data) => {
      console.log('handleLocationLinkUpdate received:', data);
      console.log('props.conversationId:', props.conversationId);
      console.log('internalConversationId:', internalConversationId.value);

      // Compare with both display_id and internal conversation_id
      const matchesDisplayId = data.conversationId && String(data.conversationId) === String(props.conversationId);
      const matchesInternalId = data.conversationId && String(data.conversationId) === String(internalConversationId.value);

      console.log('matchesDisplayId:', matchesDisplayId, 'matchesInternalId:', matchesInternalId);

      if (matchesDisplayId || matchesInternalId) {
        console.log('Match found! Setting locationLink to:', data.link);
        locationLink.value = data.link || '';
        await nextTick();
        await saveQuote();
        console.log('saveQuote completed');
      }
    };

    onMounted(() => {
      emitter.on('quote:set-delivery-address', handleDeliveryAddressUpdate);
      emitter.on('quote:set-location-link', handleLocationLinkUpdate);
    });

    onUnmounted(() => {
      emitter.off('quote:set-delivery-address', handleDeliveryAddressUpdate);
      emitter.off('quote:set-location-link', handleLocationLinkUpdate);
    });

    return {
      t,
      isLoading,
      isSaving,
      isSending,
      items,
      deliveryAddress,
      locationLink,
      observations,
      discount,
      searchQuery,
      showProductSearch,
      searchInputRef,
      quickAddInputRef,
      quickAddName,
      quickAddPrice,
      quickAddQty,
      isAddingItem,
      expandedItemId,
      filteredProducts,
      subtotal,
      total,
      addProduct,
      quickAddItem,
      handleQuickAddKeydown,
      updateItem,
      removeItem,
      incrementQty,
      decrementQty,
      toggleItemExpanded,
      saveQuote,
      formatCurrency,
      recalculateItemTotal,
      sendQuoteToConversation,
      handleClose,
    };
  },
};
</script>

<template>
  <div class="quote-form" @submit.prevent>
    <!-- Loading -->
    <div v-if="isLoading" class="flex justify-center items-center py-12">
      <Spinner />
    </div>

    <div v-else class="space-y-4">
      <!-- Quick Add Section -->
      <div class="bg-woot-50 dark:bg-woot-900/20 rounded-lg p-3 border border-woot-200 dark:border-woot-800">
        <div class="flex items-center gap-2 mb-2">
          <span class="i-lucide-zap text-woot-500 size-4" />
          <span class="text-sm font-medium text-woot-700 dark:text-woot-300">Adicionar Item Rápido</span>
        </div>
        <!-- Nome do Item -->
        <div class="mb-2">
          <input
            ref="quickAddInputRef"
            v-model="quickAddName"
            type="text"
            placeholder="Nome do item"
            class="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-woot-500 focus:border-transparent"
            @keydown="handleQuickAddKeydown"
          />
        </div>
        <!-- Preço, Quantidade e Botão -->
        <div class="flex gap-2 items-center">
          <div class="flex-1">
            <label class="block text-xs text-slate-500 mb-1">Preço</label>
            <input
              v-model="quickAddPrice"
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              class="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-woot-500 focus:border-transparent"
              @keydown="handleQuickAddKeydown"
            />
          </div>
          <div>
            <label class="block text-xs text-slate-500 mb-1">Qtd</label>
            <div class="flex items-center bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg">
              <button
                type="button"
                class="px-2 py-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-l-lg"
                @click.prevent.stop="quickAddQty = Math.max(1, quickAddQty - 1)"
              >
                <span class="i-lucide-minus size-4" />
              </button>
              <span class="w-8 text-sm text-center text-slate-800 dark:text-slate-200">{{ quickAddQty }}</span>
              <button
                type="button"
                class="px-2 py-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-r-lg"
                @click.prevent.stop="quickAddQty++"
              >
                <span class="i-lucide-plus size-4" />
              </button>
            </div>
          </div>
          <div>
            <label class="block text-xs text-slate-500 mb-1">&nbsp;</label>
            <button
              type="button"
              :disabled="isAddingItem || !quickAddName.trim()"
              class="px-4 py-2 bg-woot-500 hover:bg-woot-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-lg flex items-center gap-2 transition-colors"
              @click.prevent.stop="quickAddItem"
            >
              <span v-if="isAddingItem" class="i-lucide-loader-2 size-4 animate-spin" />
              <span v-else class="i-lucide-plus size-4" />
              <span class="text-sm">Adicionar</span>
            </button>
          </div>
        </div>

        <!-- Product Search Toggle -->
        <button
          type="button"
          class="mt-2 text-sm text-woot-600 dark:text-woot-400 hover:text-woot-700 dark:hover:text-woot-300 flex items-center gap-1"
          @click.prevent.stop="showProductSearch = !showProductSearch"
        >
          <span class="i-lucide-search size-3" />
          {{ showProductSearch ? 'Ocultar produtos' : 'Buscar produto cadastrado' }}
        </button>

        <!-- Product Search -->
        <div
          v-if="showProductSearch"
          class="mt-3 pt-3 border-t border-woot-200 dark:border-woot-800"
        >
          <input
            ref="searchInputRef"
            v-model="searchQuery"
            type="text"
            class="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 mb-2"
            placeholder="Buscar por nome ou código..."
          />
          <div class="max-h-40 overflow-auto space-y-1">
            <div
              v-for="product in filteredProducts"
              :key="product.id"
              class="flex items-center justify-between p-2 hover:bg-woot-100 dark:hover:bg-woot-900/40 rounded cursor-pointer transition-colors"
              @click.prevent.stop="addProduct(product)"
            >
              <div class="min-w-0 flex-1">
                <p class="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">
                  {{ product.name }}
                </p>
                <p v-if="product.sku" class="text-xs text-slate-500 truncate">
                  {{ product.sku }}
                </p>
              </div>
              <span class="text-sm font-medium text-woot-600 dark:text-woot-400 ml-2 flex-shrink-0">
                {{ formatCurrency(product.current_price || product.price) }}
              </span>
            </div>
            <p
              v-if="filteredProducts.length === 0"
              class="text-sm text-slate-500 text-center py-3"
            >
              Nenhum produto encontrado
            </p>
          </div>
        </div>
      </div>

      <!-- Items List -->
      <div>
        <div class="flex items-center justify-between mb-2">
          <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
            Itens ({{ items.length }})
          </span>
          <span class="text-sm font-semibold text-green-600 dark:text-green-400">
            {{ formatCurrency(subtotal) }}
          </span>
        </div>

        <div v-if="items.length === 0" class="text-center py-6 text-slate-400 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
          <span class="i-lucide-package text-3xl mb-2 block" />
          <p class="text-sm">Nenhum item adicionado</p>
        </div>

        <div v-else class="space-y-2">
          <div
            v-for="item in items"
            :key="item.id"
            class="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden"
          >
            <!-- Compact Item Row -->
            <div class="flex items-center gap-2 p-2">
              <!-- Item Info -->
              <div
                class="flex-1 min-w-0 cursor-pointer"
                @click.prevent.stop="toggleItemExpanded(item.id)"
              >
                <div class="flex items-center gap-2">
                  <span
                    class="i-lucide-chevron-right size-4 text-slate-400 transition-transform flex-shrink-0"
                    :class="{ 'rotate-90': expandedItemId === item.id }"
                  />
                  <span class="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">
                    {{ item.product_name }}
                  </span>
                </div>
                <div class="flex items-center gap-2 ml-6 text-xs text-slate-500">
                  <span>{{ formatCurrency(item.unit_price) }}</span>
                  <span>×</span>
                  <span>{{ item.quantity }}</span>
                  <span v-if="item.discount > 0" class="text-green-600">
                    (-{{ formatCurrency(item.discount) }})
                  </span>
                </div>
              </div>

              <!-- Quantity Controls -->
              <div class="flex items-center bg-slate-100 dark:bg-slate-700 rounded-lg flex-shrink-0">
                <button
                  type="button"
                  class="p-1.5 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-l-lg transition-colors"
                  @click.prevent.stop="decrementQty(item, $event)"
                >
                  <span class="i-lucide-minus size-4" />
                </button>
                <span class="px-2 text-sm font-medium text-slate-800 dark:text-slate-200 min-w-[2rem] text-center">
                  {{ item.quantity }}
                </span>
                <button
                  type="button"
                  class="p-1.5 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-r-lg transition-colors"
                  @click.prevent.stop="incrementQty(item, $event)"
                >
                  <span class="i-lucide-plus size-4" />
                </button>
              </div>

              <!-- Item Total -->
              <span class="text-sm font-semibold text-slate-800 dark:text-slate-200 min-w-[70px] text-right">
                {{ formatCurrency(item.total) }}
              </span>

              <!-- Delete Button -->
              <button
                type="button"
                class="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors flex-shrink-0"
                @click.prevent.stop="removeItem(item, $event)"
              >
                <span class="i-lucide-x size-4" />
              </button>
            </div>

            <!-- Expanded Edit Section -->
            <div
              v-if="expandedItemId === item.id"
              class="px-3 pb-3 pt-1 border-t border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50"
            >
              <div class="grid grid-cols-3 gap-2 mb-2">
                <div>
                  <label class="block text-xs text-slate-500 mb-1">Preço Unit.</label>
                  <input
                    v-model.number="item.unit_price"
                    type="number"
                    step="0.01"
                    min="0"
                    class="w-full px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200"
                    @input="recalculateItemTotal(item)"
                    @blur="updateItem(item)"
                  />
                </div>
                <div>
                  <label class="block text-xs text-slate-500 mb-1">Quantidade</label>
                  <input
                    v-model.number="item.quantity"
                    type="number"
                    min="1"
                    class="w-full px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200"
                    @input="recalculateItemTotal(item)"
                    @blur="updateItem(item)"
                  />
                </div>
                <div>
                  <label class="block text-xs text-slate-500 mb-1">Desconto</label>
                  <input
                    v-model.number="item.discount"
                    type="number"
                    step="0.01"
                    min="0"
                    class="w-full px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200"
                    @input="recalculateItemTotal(item)"
                    @blur="updateItem(item)"
                  />
                </div>
              </div>
              <div>
                <label class="block text-xs text-slate-500 mb-1">Nome do Item</label>
                <input
                  v-model="item.product_name"
                  type="text"
                  class="w-full px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 mb-2"
                  @blur="updateItem(item)"
                />
              </div>
              <div v-if="item.product_description" class="mb-2">
                <label class="block text-xs text-slate-500 mb-1">Descrição do Produto</label>
                <p class="text-xs text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 px-2 py-1.5 rounded">
                  {{ item.product_description }}
                </p>
              </div>
              <div>
                <label class="block text-xs text-slate-500 mb-1">Observação</label>
                <input
                  v-model="item.observation"
                  type="text"
                  placeholder="Observação do item..."
                  class="w-full px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-400"
                  @blur="updateItem(item)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Totals -->
      <div class="bg-slate-100 dark:bg-slate-800 rounded-lg p-3">
        <div class="flex justify-between items-center text-sm mb-2">
          <span class="text-slate-600 dark:text-slate-400">Desconto Geral</span>
          <div class="flex items-center gap-1">
            <span class="text-slate-400">R$</span>
            <input
              v-model.number="discount"
              type="number"
              step="0.01"
              min="0"
              class="w-20 px-2 py-1 text-sm text-right border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200"
              @blur="saveQuote"
            />
          </div>
        </div>
        <div class="flex justify-between items-center pt-2 border-t border-slate-200 dark:border-slate-700">
          <span class="text-lg font-semibold text-slate-800 dark:text-slate-200">Total</span>
          <span class="text-xl font-bold text-green-600 dark:text-green-400">{{ formatCurrency(total) }}</span>
        </div>
      </div>

      <!-- Delivery Address & Observations -->
      <details class="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
        <summary class="px-3 py-2 cursor-pointer text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded-lg flex items-center gap-2 list-none">
          <span class="i-lucide-chevron-right size-4 details-arrow transition-transform" />
          <span class="i-lucide-file-text size-4" />
          Endereço e Observações
          <span v-if="deliveryAddress || locationLink || observations" class="ml-auto size-2 rounded-full bg-woot-500" />
        </summary>
        <div class="p-3 pt-0 space-y-3">
          <div>
            <label class="block text-xs text-slate-500 mb-1">Endereço de Entrega</label>
            <textarea
              v-model="deliveryAddress"
              rows="2"
              class="w-full px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200"
              placeholder="Endereço de entrega..."
              @blur="saveQuote"
            />
          </div>
          <div>
            <label class="block text-xs text-slate-500 mb-1">Link Localização</label>
            <div class="flex gap-2">
              <input
                v-model="locationLink"
                type="url"
                class="flex-1 px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200"
                placeholder="https://maps.google.com/..."
                @blur="saveQuote"
              />
              <a
                v-if="locationLink"
                :href="locationLink"
                target="_blank"
                class="px-2 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded text-sm flex items-center gap-1"
                title="Abrir no mapa"
              >
                <span class="i-lucide-map-pin size-4" />
              </a>
            </div>
          </div>
          <div>
            <label class="block text-xs text-slate-500 mb-1">Observações</label>
            <textarea
              v-model="observations"
              rows="2"
              class="w-full px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200"
              placeholder="Observações do orçamento..."
              @blur="saveQuote"
            />
          </div>
        </div>
      </details>

      <!-- Actions -->
      <div class="flex gap-2 pt-2">
        <button
          type="button"
          :disabled="items.length === 0 || isSending"
          class="flex-1 px-4 py-2.5 bg-green-500 hover:bg-green-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-lg flex items-center justify-center gap-2 transition-colors font-medium"
          @click.prevent.stop="sendQuoteToConversation"
        >
          <span v-if="isSending" class="i-lucide-loader-2 size-4 animate-spin" />
          <span v-else class="i-lucide-send size-4" />
          Enviar Orçamento
        </button>
        <button
          type="button"
          class="px-4 py-2.5 bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-lg transition-colors"
          @click.prevent.stop="handleClose"
        >
          <span class="i-lucide-x size-4" />
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.quote-form {
  max-height: calc(100vh - 280px);
  overflow-y: auto;
  padding-bottom: 0.5rem;
}

/* Hide number input spinners */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  appearance: none;
  margin: 0;
}
input[type="number"] {
  -moz-appearance: textfield;
  appearance: textfield;
}

/* Details arrow rotation */
details[open] .details-arrow {
  transform: rotate(90deg);
}

details summary::-webkit-details-marker {
  display: none;
}
</style>
